import React, { useState, useEffect } from 'react';
import './Notifications.css';
import { api } from '../utils/api';
import { getParsedUser } from '../utils/authStorage';

const Notifications = ({ isOpen, onClose, onUnreadCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load read status from localStorage
  const getStoredReadStatus = () => {
    try {
      const stored = localStorage.getItem('raheasy_notifications_read');
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  };

  const [readStatus, setReadStatus] = useState(getStoredReadStatus);

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
    return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  };

  // Fetch notifications from API
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isLoggedIn = !!getParsedUser();
      
      // Fetch active notifications
      const response = await api.get('/notifications/active');
      
      let notificationsData = [];
      if (Array.isArray(response)) {
        notificationsData = response;
      } else if (response.data && Array.isArray(response.data)) {
        notificationsData = response.data;
      } else if (response.notifications && Array.isArray(response.notifications)) {
        notificationsData = response.notifications;
      }

      // Filter by target audience
      const filteredNotifications = notificationsData.filter(notif => {
        if (notif.targetAudience === 'all') return true;
        if (notif.targetAudience === 'registered' && isLoggedIn) return true;
        if (notif.targetAudience === 'guests' && !isLoggedIn) return true;
        return false;
      });

      // Transform notifications to include formatted time and read status
      const transformedNotifications = filteredNotifications.map(notif => {
        const notifId = notif._id || notif.id;
        const isRead = readStatus[notifId] === false;
        
        return {
          id: notifId,
          title: notif.title,
          message: notif.message,
          type: notif.type || 'info',
          link: notif.link || '',
          time: formatTimeAgo(notif.createdAt),
          unread: !isRead,
          createdAt: notif.createdAt
        };
      });

      setNotifications(transformedNotifications);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-refresh notifications every 5 minutes when modal is open
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      fetchNotifications();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Save read status to localStorage
  const saveReadStatus = (status) => {
    localStorage.setItem('raheasy_notifications_read', JSON.stringify(status));
    setReadStatus(status);
  };

  // Calculate unread count
  const getUnreadCount = () => {
    return notifications.filter(notif => {
      const notifId = notif.id;
      if (readStatus[notifId] === false) return false; // explicitly marked as read
      return notif.unread; // use default from API
    }).length;
  };

  // Notify parent of unread count changes
  useEffect(() => {
    if (onUnreadCountChange && !loading) {
      onUnreadCountChange(getUnreadCount());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readStatus, notifications, loading]);

  const handleMarkAllAsRead = () => {
    const newReadStatus = { ...readStatus };
    notifications.forEach(notif => {
      newReadStatus[notif.id] = false; // false means read
    });
    saveReadStatus(newReadStatus);
  };

  const handleNotificationClick = (notification) => {
    const newReadStatus = { ...readStatus };
    newReadStatus[notification.id] = false; // Mark as read
    saveReadStatus(newReadStatus);
    
    // Navigate to link if provided
    if (notification.link) {
      window.location.href = notification.link;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="notifications-overlay" onClick={onClose}>
      <div className="notifications-panel" onClick={(e) => e.stopPropagation()}>
        <div className="notifications-header">
          <h3>Notifications</h3>
          <button className="close-notifications" onClick={onClose}>×</button>
        </div>
        <div className="notifications-list">
          {loading ? (
            <div className="no-notifications">
              <p>Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="no-notifications">
              <p>{error}</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="no-notifications">
              <p>No notifications</p>
            </div>
          ) : (
            notifications.map(notification => (
              <div 
                key={notification.id} 
                className={`notification-item ${notification.unread ? 'unread' : ''} notification-type-${notification.type}`}
                onClick={() => handleNotificationClick(notification)}
                style={{ cursor: notification.link ? 'pointer' : 'default' }}
              >
                <div className="notification-content">
                  <h4>{notification.title}</h4>
                  <p>{notification.message}</p>
                  <span className="notification-time">{notification.time}</span>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="notifications-footer">
          <button 
            className="mark-all-read" 
            onClick={handleMarkAllAsRead}
            disabled={notifications.every(n => !n.unread) || loading}
          >
            Mark all as read
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notifications;

